import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../../types/bonus';
import { RecognizeResponse } from '../../../types/api';
import { Reaction } from '../lib/reaction';
import { ReactionType } from '../types';
import { buildReaction } from '../utils';
import { useAddReactionToBonus } from './use-add-reaction-to-bonus';
import { useRemoveReactionFromBonus } from './use-remove-reaction-from-bonus';
import { useReplaceBonus } from './use-replace-bonus';

export const useReplaceReaction = (bonus: Bonus) => {
	const user = useCurrentUser();
	const addReaction = useAddReactionToBonus();
	const removeReaction = useRemoveReactionFromBonus();
	const replaceBonus = useReplaceBonus();
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation(
		async ({ from, to }: { from: Reaction; to: ReactionType }) => {
			const reaction = Reaction.fromType(to);

			await http.delete<RecognizeResponse<Bonus>>(`reactions/${from.id}`, {
				params: {
					bonus_id: bonus.id,
					emoji: from.shortcode,
				},
			});

			return http.post<RecognizeResponse<Bonus>>('reactions', {
				bonus_id: bonus.id,
				emoji: reaction.shortcode,
			});
		},
		{
			onMutate: ({ from, to }) => {
				const record = buildReaction(to, user);

				const removed = removeReaction(bonus.id, from.id);
				addReaction(bonus.id, record);

				return { tempId: record._id, removed };
			},
			onError: (_err, _args, ctx) => {
				if (ctx && ctx.removed) {
					const { tempId, removed } = ctx;

					removeReaction(bonus.id, tempId);
					addReaction(bonus.id, removed);
				}
			},
			onSuccess: (data) => {
				replaceBonus(data.data.result);
				queryClient.invalidateQueries(['reactions', bonus.id]);
			},
		}
	);
};
